import { useDispatch, useSelector } from 'react-redux';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo, useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { selectSession, selectFeatureFlags } from '@client/model/selectors';
import { fetchSessionForAuthRequest } from '@client/model';
import { advisorOnboardingKeys } from '@client/api/waterlily';
import { setHasSkippedOnboardingStep } from '@client/model/advisorOnboardingModel';
import { useFeatureFlags } from '@/contexts/FeatureFlagsContext';
import { isQuestionnaireRequiredForAdvisor } from '@shared';

export interface AdvisorOnboardingStepConfig {
  id: AdvisorOnboardingUIStep;
  title: string;
  path: string;
}

const ALL_ONBOARDING_STEP_CONFIGS: AdvisorOnboardingStepConfig[] = [
  { id: 'basic-info', title: 'Personal details', path: 'basic-info' },
  { id: 'questionnaire', title: 'Personalize your setup', path: 'questionnaire' },
  { id: 'activate-seat', title: 'Activate seat', path: 'activate-seat' },
  { id: 'consent', title: 'Terms of Service', path: 'consent' },
  { id: 'connect-email', title: 'Connect email', path: 'connect-email' },
  { id: 'first-plan', title: 'Create your first plan', path: 'first-plan' },
];

const EDJ_PILOT_EXCLUDED_STEPS: AdvisorOnboardingUIStep[] = ['connect-email', 'consent'];

export const ONBOARDING_STEP_CONFIGS = ALL_ONBOARDING_STEP_CONFIGS;

export function getOnboardingStepConfigs(
  featureFlags: FeatureFlags,
  advisor?: { advisorCreatedDateTime?: Date | string | number | null } | null,
  appConfigFlags?: AppConfigFeatureFlags,
): AdvisorOnboardingStepConfig[] {
  let steps = ALL_ONBOARDING_STEP_CONFIGS;

  if (featureFlags.edjPilotAdvisor) {
    steps = steps.filter(s => !EDJ_PILOT_EXCLUDED_STEPS.includes(s.id));
  }

  const questionnaireFlagOn = !!appConfigFlags?.advisorOnboardingQuestionnaire;

  if (!questionnaireFlagOn || !isQuestionnaireRequiredForAdvisor(advisor ?? null)) {
    steps = steps.filter(s => s.id !== 'questionnaire');
  }

  return steps;
}

export interface OnboardingNavigationOptions {
  advisorId: string;
  currentStep: AdvisorOnboardingUIStep;
  markStepAsCompleted: (step: string) => void;
}

export function useOnboardingNavigation({
  advisorId,
  currentStep,
  markStepAsCompleted,
}: OnboardingNavigationOptions) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const session = useSelector(selectSession);
  const featureFlags = useSelector(selectFeatureFlags);
  const { flags: appConfigFlags } = useFeatureFlags();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isPendingDashboardNavigation, setIsPendingDashboardNavigation] = useState(false);

  const steps = useMemo(
    () => getOnboardingStepConfigs(featureFlags, session.advisor, appConfigFlags),
    [featureFlags, session.advisor, appConfigFlags],
  );

  const currentStepIndex = steps.findIndex(el => el.id === currentStep);
  const currentStepConfig = steps[currentStepIndex];
  const isLastStep = currentStepIndex === steps.length - 1;

  // Navigate to dashboard once session is refreshed and forceRedirect is cleared
  useEffect(() => {
    if (isPendingDashboardNavigation && !session.isInvalidating && !session.forceRedirect) {
      setIsPendingDashboardNavigation(false);
      // Invalidate onboarding status so the quick start guide reflects the new state
      queryClient.invalidateQueries({ queryKey: advisorOnboardingKeys.status(advisorId) });
      navigate(`/advisors/${advisorId}/clients`);
    }
  }, [
    isPendingDashboardNavigation,
    session.isInvalidating,
    session.forceRedirect,
    advisorId,
    navigate,
    queryClient,
  ]);

  const goToStep = useCallback(
    (step: AdvisorOnboardingUIStep) => {
      const stepConfig = steps.find(el => el.id === step);
      if (stepConfig) {
        navigate(`/advisors/${advisorId}/onboarding/${stepConfig.path}`);
      }
    },
    [advisorId, navigate, steps],
  );

  const goNext = useCallback(() => {
    if (currentStepIndex < steps.length - 1) {
      markStepAsCompleted(currentStep);
      goToStep(steps[currentStepIndex + 1].id);
    }
  }, [goToStep, currentStepIndex, currentStep, markStepAsCompleted, steps]);

  const goPrev = useCallback(() => {
    if (currentStep === 'first-plan' && searchParams.get('view')) {
      setSearchParams({}, { replace: false });
      return;
    }

    if (currentStepIndex > 0) {
      goToStep(steps[currentStepIndex - 1].id);
    }
  }, [goToStep, currentStepIndex, currentStep, searchParams, setSearchParams, steps]);

  const skip = useCallback(() => {
    dispatch(setHasSkippedOnboardingStep(true));
    if (currentStepIndex < steps.length - 1) {
      goToStep(steps[currentStepIndex + 1].id);
    } else {
      // Last step - recompute onboarding status, then refresh session and go to dashboard
      setIsPendingDashboardNavigation(true);
      queryClient
        .fetchQuery({ queryKey: advisorOnboardingKeys.status(advisorId) })
        .finally(() => dispatch(fetchSessionForAuthRequest({ invalidating: true })));
    }
  }, [goToStep, currentStepIndex, dispatch, queryClient, advisorId, steps]);

  const goToDashboard = useCallback(() => {
    navigate(`/advisors/${advisorId}/clients`);
  }, [advisorId, navigate]);

  const refreshSessionAndGoToDashboard = useCallback(() => {
    // Show loading immediately, then recompute onboarding status so the server persists the
    // latest status before maybeLoadSession reads it during the session refresh.
    setIsPendingDashboardNavigation(true);
    queryClient
      .fetchQuery({ queryKey: advisorOnboardingKeys.status(advisorId) })
      .finally(() => dispatch(fetchSessionForAuthRequest({ invalidating: true })));
  }, [dispatch, queryClient, advisorId]);

  return {
    currentStepIndex,
    currentStepConfig,
    isLastStep,
    goNext,
    goPrev,
    skip,
    goToDashboard,
    goToStep,
    refreshSessionAndGoToDashboard,
    isPendingDashboardNavigation,
  };
}
